import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { AtsSkeleton, Button, BUTTON_TYPES, Card, notification, NOTIFICATION_TYPES, Tooltip } from '_atoms';
import { Priority } from '_atoms/Priority';
import type { PriorityValue } from '_atoms/Priority';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import { OPENING_STATUS_TYPES } from 'constants/opening';
import { getRecruiterNewOpeningsDashboardAPI, markRecruiterNewOpeningReadAPI } from 'actions';
import { ReactComponent as JobImage } from 'dist/emptyStates/dashboardOpening.svg';
import './style.scss';
import { useTranslation } from 'react-i18next';
import type { DashboardNewOpening } from 'actions/dashboardActions';
import type { Uuid } from 'store/reducers/types';

type OpeningRow = DashboardNewOpening & { level?: string };

const getOpeningTitle = (opening: OpeningRow) => [opening.level, opening.job_name].filter(Boolean).join(' ');

export const NewOpenings: React.FC = React.memo(() => {
  const [openings, setOpenings] = useState<OpeningRow[]>([]);
  const [allShown, setAllShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingUuid, setMarkingUuid] = useState<Uuid | null>(null);
  const { t } = useTranslation();

  const onShowMoreClick = () => setAllShown(s => !s);

  const fetchOpenings = useCallback(() => {
    setLoading(true);
    getRecruiterNewOpeningsDashboardAPI()
      .then(data => setOpenings(data as unknown as OpeningRow[]))
      .catch(() => setOpenings([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchOpenings();
  }, [fetchOpenings]);

  const onMarkAsRead = (openingUuid: Uuid) => {
    setMarkingUuid(openingUuid);
    markRecruiterNewOpeningReadAPI(openingUuid)
      .then(() => {
        notification({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: t('translation:new_openings.notification_marked_read'),
        });
        fetchOpenings();
      })
      .finally(() => setMarkingUuid(null));
  };

  if (loading) {
    return (
      <Card className="new-recruiter-openings">
        <div className="new-recruiter-openings__header">
          {t('translation:new_openings.title')}
          <span className="number">{openings.length}</span>
        </div>
        <div className="new-recruiter-openings__content-loading">
          <AtsSkeleton height={140} />
        </div>
      </Card>
    );
  }

  if (!openings.length) {
    return (
      <Card className="new-recruiter-openings">
        <div className="new-recruiter-openings__header">
          {t('translation:new_openings.title')}
          <span className="number">0</span>
        </div>
        <div className="new-recruiter-openings__content-empty">
          <JobImage className="empty-image" />
          <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
            {t('translation:new_openings.text_empty')}
          </Typography.Title3>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('new-recruiter-openings', { allShown })}>
      <div className="new-recruiter-openings__header">
        {t('translation:new_openings.title')}
        <span className="number">({openings.length})</span>
      </div>
      <div className="new-recruiter-openings__content">
        {openings.map(opening => {
          const title = getOpeningTitle(opening);
          return (
            <div key={opening.uuid} className="new-recruiter-openings__row">
              <div className="new-recruiter-openings__row-body">
                <div className="active-opening">
                  <Priority priority={opening.priority as PriorityValue} />
                  <Tooltip label={title}>
                    <a
                      href={`/jobs/${opening.job_uuid}/show/openings?scroll=${opening.uuid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {title}
                    </a>
                  </Tooltip>
                  <Typography.Caption className="active-opening__service">{opening.service}</Typography.Caption>
                  <Status className="active-opening__status" type={OPENING_STATUS_TYPES[opening.status]}>
                    {opening.status}
                  </Status>
                </div>
              </div>
              <div className="new-recruiter-openings__row-action">
                <Button
                  type={BUTTON_TYPES.GHOST}
                  loading={markingUuid === opening.uuid}
                  onClick={() => onMarkAsRead(opening.uuid)}
                >
                  {t('translation:new_openings.button_mark_read')}
                </Button>
              </div>
            </div>
          );
        })}
        {openings.length > 3 && (
          <Button className="new-recruiter-openings__show-more" type={BUTTON_TYPES.TEXT} onClick={onShowMoreClick}>
            {allShown ? t('translation:new_jobs.button_show_less') : t('translation:new_jobs.button_show_more')}
          </Button>
        )}
      </div>
    </Card>
  );
});
